import { ErrorBoundary as DataDogErrorBoundry } from '@datadog/browser-rum-react';
import { FC, ReactNode } from 'react';
import { APP_CRASH_KEY } from 'constant';
import { logger } from 'services';

// `{...error}` would have lost `.message`, `.stack`, `.name` (non-enumerable on
// Error) — we pass the real Error so `logger.error` → `datadogRum.addError`
// lifts the standard fields, and the explicit `errorStack` / `errorName`
// context fields make stack + identity queryable from DD Logs too (where RUM
// is gated to leadsquared/nitro).
//
// Note: DD's `@datadog/browser-rum-react` `ErrorBoundary` renders the fallback
// via `React.createElement(fallback, { error, resetError })` — i.e., a single
// props object, not positional args. See node_modules/@datadog/browser-rum-react
// /cjs/domain/error/errorBoundary.js (`render()`). The React componentStack
// is captured separately by DD's `componentDidCatch → addReactError` for RUM;
// it isn't exposed to the fallback.
const defaultOnError = (error: Error) => {
  logger.error(
    `${APP_CRASH_KEY}: Component error - ${error?.message ?? 'Unknown'}`,
    {
      error,
      errorStack: error?.stack,
      errorName: error?.name,
    },
  );
};

export const ErrorBoundary: FC<
  React.PropsWithChildren<{
    fallback?: (error?: Error) => ReactNode;
    onError?: () => void;
    children?: ReactNode;
  }>
> = ({ fallback, onError, children }) => {
  const _fallback = ({
    error,
  }: {
    error: Error;
    resetError: () => void;
  }): ReactNode => {
    if (onError) {
      onError();
    } else {
      defaultOnError(error);
    }

    return fallback?.(error) ?? null;
  };

  return (
    <DataDogErrorBoundry fallback={_fallback}>{children}</DataDogErrorBoundry>
  );
};
